/**
 * Shared soaks: a soak telegraph (intent 'soak') whose damage is split among the eligible units
 * inside. Too few soakers run `failEffects` once. 'enter' soaks (Bone Shards) resolve on the first
 * eligible unit to step in and can consume aura stacks.
 */
import type { EffectCtx, SimContext, SoakMechanic, SoakState, UnitId, Vec2, WorldShape } from '../../contracts';
import { angleTo, dist, endTelegraph, markersOf, mstate, pushTelegraph, resolveKeyScaled, runEffects, scaleAmount, unitOf } from './state';

export function applySoak(ctx: SimContext, spec: SoakMechanic, ectx: EffectCtx): void {
  const src = unitOf(ctx, ectx.sourceId);
  if (!src) return;
  const target = unitOf(ctx, ectx.targetId);
  const min = Math.max(1, Math.round(resolveKeyScaled(ctx, spec.min, 1)));
  const at = spec.at;
  const areas: WorldShape[] = [];
  const shapeAt = (origin: Vec2, facing: number): WorldShape => ({ shape: { ...spec.shape }, origin: { x: origin.x, y: origin.y }, facing });
  if (at === 'caster') areas.push(shapeAt(src.pos, src.facing));
  else if (at === 'target') {
    const p = target?.pos ?? ectx.point ?? src.pos;
    areas.push(shapeAt(p, angleTo(src.pos, p)));
  } else if (at === 'point') {
    const p = ectx.point ?? target?.pos ?? src.pos;
    areas.push(shapeAt(p, angleTo(src.pos, p)));
  } else if ('around' in at) {
    const c = at.around === 'target' && target ? target.pos : src.pos;
    const n = Math.max(1, Math.round(resolveKeyScaled(ctx, spec.count, 1)));
    // Evenly spread around a random start so two soaks never stack on each other.
    const start = ctx.rng.range(0, 2 * Math.PI);
    for (let i = 0; i < n; i++) {
      const ang = start + (2 * Math.PI * i) / n;
      const d = ctx.rng.range(at.minDist, Math.max(at.minDist, at.maxDist));
      const p = ctx.sys.spatial.clampToWalkable(ctx.world, { x: c.x + Math.cos(ang) * d, y: c.y + Math.sin(ang) * d });
      areas.push(shapeAt(p, ang));
    }
  } else {
    const marks = markersOf(ctx, src, at.marker);
    const n = spec.count === undefined ? marks.length : Math.min(marks.length, Math.round(resolveKeyScaled(ctx, spec.count, marks.length)));
    for (const m of marks.slice(0, n)) {
      const facing = angleTo(m, src.pos);
      const a = shapeAt(m, facing);
      // Beams from a brazier to the boss: the line's length is the gap between them.
      if (at.aimAtCaster && a.shape.type === 'line') a.shape = { ...a.shape, length: Math.max(2, dist(m, src.pos)) };
      areas.push(a);
    }
  }
  const st = mstate(ctx.world);
  for (const area of areas) {
    const tg = pushTelegraph(ctx, src.id, ectx.abilityId, area, ctx.now + spec.delayMs, {
      intent: spec.intent ?? 'soak',
      mechanic: 'soak',
      soak: { min, inside: 0 },
    });
    const s: SoakState = {
      id: tg.id,
      sourceId: src.id,
      abilityId: ectx.abilityId,
      area,
      min,
      trigger: spec.trigger ?? 'timer',
      resolvesAt: tg.resolvesAt,
      damage: spec.damage ?? null,
      split: spec.split ?? true,
      effects: spec.effects ?? [],
      failEffects: spec.failEffects ?? [],
      consumes: spec.consumes ?? null,
      eligibleAura: spec.eligible?.hasAura ?? null,
      inside: [],
    };
    st.soaks.push(s);
  }
}

/** Eligible units standing in the soak now (unitOrder-stable via spatial). */
export function soakersInside(ctx: SimContext, s: SoakState): UnitId[] {
  const ids = ctx.sys.spatial.unitsInShape(ctx.world, s.area, { affects: 'enemies', relativeTo: s.sourceId });
  if (!s.eligibleAura) return ids;
  return ids.filter((id) => ctx.world.units[id]?.auras.some((a) => a.auraId === s.eligibleAura));
}

/** Strip up to `n` stacks of `auraId` from a unit (whole aura when it runs out). */
export function consumeStacks(ctx: SimContext, unitId: UnitId, auraId: string, n: number): number {
  const u = unitOf(ctx, unitId);
  if (!u) return 0;
  let taken = 0;
  for (const inst of u.auras.filter((a) => a.auraId === auraId)) {
    const left = n - taken;
    if (left <= 0) break;
    if (inst.stacks <= left) {
      taken += inst.stacks;
      ctx.sys.combat.removeAura(ctx, u.id, { iid: inst.iid }, 'consumed');
    } else {
      inst.stacks -= left;
      taken += left;
      const kind = ctx.content.aura(auraId)?.kind ?? 'debuff';
      ctx.emit({ type: kind === 'buff' ? 'removebuffstack' : 'removedebuffstack', sourceID: inst.casterId, targetID: u.id, abilityGameID: auraId, stack: inst.stacks });
    }
  }
  return taken;
}

function resolveSoak(ctx: SimContext, s: SoakState, inside: UnitId[], failed: boolean): void {
  const ectx: EffectCtx = { sourceId: s.sourceId, abilityId: s.abilityId, targetId: s.sourceId, point: { ...s.area.origin }, depth: 1 };
  if (failed) runEffects(ctx, s.failEffects, ectx);
  if (inside.length > 0) {
    const share = s.split ? 1 / inside.length : 1;
    const dmg = s.damage ? [{ type: 'damage' as const, school: s.damage.school, amount: scaleAmount(s.damage.amount, share), avoidable: false, isAoE: true }] : [];
    for (const id of inside) {
      if (s.consumes) consumeStacks(ctx, id, s.consumes.auraId, s.consumes.stacks);
      const u = ctx.world.units[id];
      const per: EffectCtx = { ...ectx, targetId: id, point: u ? { x: u.pos.x, y: u.pos.y } : ectx.point };
      runEffects(ctx, dmg, per);
      runEffects(ctx, s.effects, per);
    }
  }
  endTelegraph(ctx, s.id, inside);
}

export function updateSoaks(ctx: SimContext): void {
  const st = ctx.world.mechanics;
  if (!st || st.soaks.length === 0) return;
  const done = new Set<string>();
  for (const s of [...st.soaks]) {
    const src = unitOf(ctx, s.sourceId);
    const tg = ctx.world.telegraphs.find((t) => t.id === s.id);
    if (!tg || !src?.alive) {
      if (tg) endTelegraph(ctx, s.id, []);
      done.add(s.id);
      continue;
    }
    const inside = soakersInside(ctx, s);
    s.inside = inside;
    tg.soak = { min: s.min, inside: inside.length };
    if (s.trigger === 'enter') {
      if (inside.length > 0) {
        resolveSoak(ctx, s, inside, false);
        done.add(s.id);
      } else if (ctx.now >= s.resolvesAt) {
        resolveSoak(ctx, s, [], s.failEffects.length > 0);
        done.add(s.id);
      }
      continue;
    }
    if (ctx.now >= s.resolvesAt) {
      resolveSoak(ctx, s, inside, inside.length < s.min);
      done.add(s.id);
    }
  }
  if (done.size > 0) st.soaks = st.soaks.filter((s) => !done.has(s.id));
}
